import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

import {observabilityService} from '@/services/observabilityService';
import type {TraceLogs, TracesData} from '@/types/Observability';

export default function useUserLogs() {
  const params = useParams();
  const userId = params?.userId || '';
  const limit = 10;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [traces, setTraces] = useState<TracesData[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [allTraces, setAllTraces] = useState<TracesData[]>([]);
  const [sliceStart, setSliceStart] = useState<number>(0);
  const [sliceEnd, setSliceEnd] = useState<number>(limit);

  // Pure functions of the slice window and the loaded traces, so they are
  // derived during render rather than mirrored into state by an effect.
  const canGoNext = sliceEnd < allTraces.length;
  const canGoPrev = sliceStart > 0;

  const fetchTraces = async (cursor?: string): Promise<void> => {
    try {
      setIsLoading(true);
      const traces = await observabilityService.getTraces(userId, cursor);
      setTraces(traces.data);
      setCursor(traces.cursor ?? null);
      setAllTraces((prevTraces = []) => {
        const combined = [...prevTraces, ...traces.data];
        const unique = combined.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.traceId === item.traceId),
        );
        return unique;
      });
      if (cursor) {
        setSliceStart(allTraces.length);
        setSliceEnd(allTraces.length + limit);
      }
    } catch (error) {
      console.error('Failed to fetch traces:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTraceLogs = async (traceId: string): Promise<TraceLogs> => {
    try {
      const traceLogs = await observabilityService.getTraceLogs(traceId);
      return traceLogs;
    } catch (error) {
      console.error('Failed to fetch traces:', error);
      return {data: [], limit: 0};
    }
  };

  const sliceTraces = (action: string) => {
    let newStart = sliceStart;
    let newEnd = sliceEnd;

    if (action === 'next') {
      newStart += limit;
      newEnd += limit;
    } else if (action === 'prev' && sliceStart >= limit) {
      newStart -= limit;
      newEnd -= limit;
    }

    setSliceStart(newStart);
    setSliceEnd(newEnd);
    setTraces(allTraces?.slice(newStart, newEnd));
  };

  useEffect(() => {
    // Fire-and-forget: fetchTraces try/catches, logs the failure and clears
    // `isLoading`, so this only stops an unexpected throw becoming an
    // unhandled rejection.
    fetchTraces().catch(() => {});
  }, [userId]);

  return {
    traces,
    fetchTraces,
    fetchTraceLogs,
    isLoading,
    sliceTraces,
    cursor,
    canGoNext,
    canGoPrev,
  };
}
